// src/pages/admin/PromotionsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, ToggleRight, ToggleLeft } from 'lucide-react'
import { adminPromotionsApi } from '../../api/services'
import { Card, CardBody, Button, Badge, PageSpinner } from '../../components/common/ui'
import toast from 'react-hot-toast'

const INIT = { title:'', subtitle:'', placement:'banner', link_url:'', starts_at:'', ends_at:'', is_active:true, sort_order:0 }

export default function PromotionsPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(INIT)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type==='checkbox' ? e.target.checked : e.target.value }))

  const { data, isLoading } = useQuery({ queryKey: ['admin-promotions'], queryFn: () => adminPromotionsApi.list() })
  const save = useMutation({ mutationFn: () => editing==='new' ? adminPromotionsApi.create(form) : adminPromotionsApi.update(editing, form), onSuccess: () => { toast.success('Saved'); qc.invalidateQueries(['admin-promotions']); setEditing(null) } })
  const del = useMutation({ mutationFn: id => adminPromotionsApi.delete(id), onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['admin-promotions']) } })
  const toggle = useMutation({ mutationFn: ({ id, is_active }) => adminPromotionsApi.update(id, { is_active: !is_active }), onSuccess: () => qc.invalidateQueries(['admin-promotions']) })

  const promotions = data?.data?.data || []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold">Promotions</h1>
        <Button size="sm" onClick={() => { setEditing('new'); setForm(INIT) }}><Plus size={14} /> New Promotion</Button>
      </div>

      {editing && (
        <Card className="mb-6">
          <CardBody className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <input className="input" placeholder="Title *" value={form.title} onChange={set('title')} />
              <select className="input" value={form.placement} onChange={set('placement')}>
                {['banner','sidebar','popup','email'].map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
              </select>
            </div>
            <input className="input w-full" placeholder="Subtitle" value={form.subtitle} onChange={set('subtitle')} />
            <input className="input w-full" placeholder="Link URL" value={form.link_url} onChange={set('link_url')} />
            <div className="grid sm:grid-cols-2 gap-3">
              <input className="input" type="date" value={form.starts_at} onChange={set('starts_at')} />
              <input className="input" type="date" value={form.ends_at} onChange={set('ends_at')} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.is_active} onChange={set('is_active')} /> Active</label>
            <div className="flex gap-2">
              <Button loading={save.isPending} onClick={() => save.mutate()} disabled={!form.title}>Save Promotion</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </CardBody>
        </Card>
      )}

      {isLoading && <PageSpinner />}
      <div className="space-y-2">
        {promotions.map(p => (
          <Card key={p.id}>
            <CardBody className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm">{p.title}</span>
                  <Badge variant="grey" className="capitalize">{p.placement}</Badge>
                  {!p.is_active && <Badge variant="red">Inactive</Badge>}
                </div>
                {p.starts_at && <p className="text-xs text-muted">{new Date(p.starts_at).toLocaleDateString('en-IN')} – {p.ends_at ? new Date(p.ends_at).toLocaleDateString('en-IN') : 'No end'}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle.mutate({ id: p.id, is_active: p.is_active })} className="p-1.5">{p.is_active ? <ToggleRight size={18} className="text-green-500"/> : <ToggleLeft size={18} className="text-muted"/>}</button>
                <button onClick={() => { setEditing(p.id); setForm({ ...INIT, ...p }) }} className="p-1.5"><Pencil size={14} className="text-muted hover:text-pink"/></button>
                <button onClick={() => del.mutate(p.id)} className="p-1.5"><Trash2 size={14} className="text-muted hover:text-red-500"/></button>
              </div>
            </CardBody>
          </Card>
        ))}
        {!isLoading && promotions.length === 0 && <p className="text-center text-muted py-8">No promotions yet.</p>}
      </div>
    </div>
  )
}
